import { getterTree, mutationTree, actionTree } from 'typed-vuex'
import { convertLabelValue as convert } from '~/lib/utils/format'

const hasProperty = (obj, property) => Object.prototype.hasOwnProperty.call(obj, property)

export const yesOrNo = [
  { value: 1, label: 'Yes' },
  { value: 0, label: 'No' }
]

export const state = () => ({
  loading: {
    countries: false,
    states: false,
    genders: false,
    citizenships: false,
    ethnicities: false,
    gpas: false,
    degrees: false,
    degreeTypes: false,
    careerGoals: false,
    schoolLevels: false,
    studyOnline: false,
    studyCountries: false,
    profileTypes: false,
    militaryAffiliations: false
  },
  options: {
    yesOrNo,
    countries: null,
    states: null,
    genders: null,
    citizenships: null,
    ethnicities: null,
    gpas: null,
    degrees: null,
    degreeTypes: null,
    careerGoals: null,
    schoolLevels: null,
    studyOnline: null,
    studyCountries: null,
    profileTypes: null,
    militaryAffiliations: null
  }
})

export const getters = getterTree(
  state,
  {
    yesOrNo({ options }) {
      return options.yesOrNo
    },
    label: ({ options }) => (name, value) => {
      if (!hasProperty(options, name)) { throw new Error(`Unknown option ${name}`) }
      return options[name][value]
    },
    option: (function() {
      const cache = {}
      return ({ options }) => (name, value) => {
        if (!hasProperty(options, name)) { throw new Error(`Unknown option ${name}`) }

        if (typeof value === 'undefined' || value === null) { return null }

        if (!hasProperty(cache, name)) { cache[name] = {} }

        if (!hasProperty(cache[name], value)) {
          cache[name][value] = { value, label: options[name][value] }
        }

        return cache[name][value]
      }
    })(),
    srcLoaded({ loading }) {
      return !Object.values(loading).includes(true)
    },
    countries({ options }) {
      return convert(options.countries)
    },
    states({ options }) {
      return convert(options.states)
    },
    genders({ options }) {
      return convert(options.genders)
    },
    citizenships({ options }) {
      return convert(options.citizenships)
    },
    ethnicities({ options }) {
      return convert(options.ethnicities)
    },
    gpas({ options }) {
      return convert(options.gpas)
    },
    degrees({ options }) {
      return convert(options.degrees)
    },
    degreeTypes({ options }) {
      return convert(options.degreeTypes)
    },
    careerGoals({ options }) {
      return convert(options.careerGoals)
    },
    schoolLevels({ options }) {
      return convert(options.schoolLevels)
    },
    studyOnline({ options }) {
      return convert(options.studyOnline)
    },
    studyCountries({ options }) {
      return convert(options.studyCountries)
    },
    profileTypes({ options }) {
      return convert(options.profileTypes)
    },
    militaryAffiliations({ options }) {
      return convert(options.militaryAffiliations)
    }
  }
)

export const mutations = mutationTree(
  state,
  {
    setOption(state, { name, data }) {
      if (!hasProperty(state.options, name)) {
        throw new Error(`Unknown option: ${name}`)
      }

      state.options[name] = data
    },
    setLoading(state, { names, loading }) {
      (typeof names === 'string' ? [names] : names).forEach((name) => {
        if (!hasProperty(state.loading, name)) {
          throw new Error(`Unknown option: ${name}`)
        }

        state.loading[name] = loading
      })
    }
  }
)

export const actions = actionTree(
  { state, getters, mutations },
  {
    load({ state, commit }, { name, callback }) {
      let names = Object.keys(state.options)

      if (typeof name === 'string') {
        names = [name]
      }

      if (Object.prototype.toString.call(name) === '[object Array]') {
        names = name
      }

      const load = names.filter((name) => {
        return hasProperty(state.options, name) &&
          !state.options[name] &&
          !state.loading[name]
      })

      if (load.length > 0) {
        commit('setLoading', { names: load, loading: true })

        return this.$api.options.get({ only: load })
          .then((response) => {
            Object.keys(response.data.data).forEach((name) => {
              const data = response.data.data[name]
              if (!data || (data && Array.isArray(data) && !data.length)) { return }
              commit('setOption', { name, data })
            })

            if (callback) { setTimeout(callback, 100) }

            return state.options
          })
          .finally(() => commit('setLoading', { names: load, loading: false }))
      }

      if (callback) { setTimeout(callback, 100) }

      return Promise.resolve([])
    }
  }
)
